import React, { useMemo } from 'react';
import { Tag } from 'tea-component';
import { getLocale } from 'umi';
import { IHighPriorityEventBlockProps } from '../types';
import { useEventItemPosition } from '../hooks/useEventItemPosition';
import { useHighPriorityMarkArea } from '../hooks/useHighPriorityMarkArea';
import { MarkArea, EventLine, EventBubble } from '../HighPriorityCommon';
import { formatRatio } from '../utils/eventBlockUtils';
import classNames from 'classnames';
import './index.scss';

export default function HighPriorityEventBlock({
  myChart,
  xAxisValue,
  block,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  onBlockClick,
  hasAnySelected = false,
  anchorMode = 'start',
  blockTitle,
  trendText,
  bubbleContent,
  onBubbleVisibleChange,
  onHighPriorityItemClick
}: IHighPriorityEventBlockProps) {
  const { events, blockTrend, avgRatio, selected, start_time, end_time } =
    block;

  // 获取当前语言环境
  const locale = getLocale() as 'zh-CN' | 'en-US';

  // 根据语言环境获取标题
  const title = blockTitle?.[locale] ?? blockTitle?.['zh-CN'] ?? '';

  // 根据语言环境获取标签名称
  const getTagName = (event: (typeof events)[0]) => {
    if (locale === 'en-US') {
      return event.tagNameEn || event.tagName;
    }
    return event.tagNameZh || event.tagName;
  };

  // 用于 useEventItemPosition 的模拟 event 对象
  const pseudoEvent = useMemo(
    () => ({
      start_time,
      end_time,
    }),
    [start_time, end_time],
  );

  const positionHook = useEventItemPosition({
    myChart,
    xAxisValue,
    format,
    dateType,
    xAxisData,
    backPositionInfo,
    yAxisMin,
    anchorMode,
    event: pseudoEvent,
  });

  const { eventBoxRef, eventLineRef } = positionHook;

  // 使用公共 hook 处理 mark area 逻辑
  const {
    isSelected,
    isDimmed,
    markAreaShow,
    renderMarkArea,
    markAreaTimes,
    eventLineShow,
    markAreaWidth,
    handleMouseEnter,
    handleMouseLeave,
  } = useHighPriorityMarkArea({
    selected,
    hasAnySelected,
    startTime: start_time,
    endTime: end_time,
    format,
    anchorMode,
    halfBarWidth,
    maxTimeValue,
    positionHook,
    myChart,
  });

  // 获取标签颜色
  const getTagTheme = (ratio?: number) => {
    if (ratio === undefined || ratio === null) return 'default';
    return ratio >= 0 ? 'success' : 'danger';
  };

  const getTrendLabel = (ratio: number) => {
    // 根据 ratio 判断趋势：负数=下降，正数或未定义=上升
    const isDown = ratio !== undefined ? ratio < 0 : false;
    // 趋势方向
    const trend = isDown ? 'down' : 'up';
    const trendLabel =
      trend && trendText?.[trend] ? trendText?.[trend]?.[locale] : undefined;
    return trendLabel;
  };

  return (
    <>
      {/* 公共组件：标记区域 */}
      <MarkArea
        show={markAreaShow}
        style={renderMarkArea}
        time={markAreaTimes}
        long={!showEventAxis}
        anchorMode={anchorMode}
        halfBarWidth={halfBarWidth}
        markAreaWidth={markAreaWidth}
        neutral
      />

      <div ref={eventBoxRef} className="dbo-high-priority-block-wrapper">
        <div
          onMouseEnter={handleMouseEnter}
          onClick={() => onBlockClick?.(block)}
          onMouseLeave={handleMouseLeave}
          className={classNames('dbo-high-priority-block', {
            '--selected': isSelected,
            '--dimmed': isDimmed,
            '--hide': hide,
          })}
        >
          {/* 标题 */}
          <div className="dbo-hp-block-title">{title}</div>

          {/* 事件标题列表 */}
          <div className="dbo-hp-block-events">
            {[...events]
              .sort((a, b) => (b.ratio ?? 0) - (a.ratio ?? 0))
              .map((event) => {
                const eventRatioInfo = formatRatio(event.ratio);
                const isEventDown = (event.ratio ?? 0) < 0;
                return (
                  <EventBubble
                    bubbleContent={bubbleContent}
                    onBubbleVisibleChange={onBubbleVisibleChange}
                    event={event}
                  >
                    <div key={event.id} className="dbo-hp-block-event-item" onClick={(e) => {
                      e.stopPropagation();
                      onHighPriorityItemClick?.(event);
                    }}>
                      <Tag
                        theme={getTagTheme(event.ratio)}
                        shapeType="rounded"
                        className="dbo-hp-block-event-tag"
                      >
                        {getTagName(event)}
                      </Tag>
                      <span className="dbo-hp-block-event-title">
                        {getTrendLabel(event.ratio ?? 0)}
                      </span>
                      {eventRatioInfo && (
                        <span
                          className={classNames('dbo-hp-block-event-ratio', {
                            '--down': isEventDown,
                            '--up': !isEventDown,
                          })}
                        >
                          {eventRatioInfo.value}
                          <span className="dbo-hp-block-event-arrow">
                            {isEventDown ? '↓' : '↑'}
                          </span>
                        </span>
                      )}
                    </div>
                  </EventBubble>
                );
              })}
          </div>
          <i className="dbo-hp-icon-logo"></i>
        </div>
      </div>

      {/* 公共组件：连接线 */}
      <EventLine
        lineRef={eventLineRef}
        highlight={eventLineShow}
        trend={blockTrend}
        selected={isSelected}
        dimmed={isDimmed}
        neutral
      />
    </>
  );
}
